/* AssembleModulesDialog — the Modules stepper (docs/MODULE_ASSEMBLY_PLAN.md §3).
   One dialog for Add to Project and Export from Masters: the same
   `ModuleBrowser` the Modules page uses, in checkbox mode, one step per
   spec. Checked = the modules the spec will carry. Done applies the picks
   for EVERY spec (visited or not) — nobody has to click through all of them.
   Permission: `modules.view` (the library is never fetched without it). */
import React from 'react';
import { Layers } from 'lucide-react';
import { Icon } from './ui/Icon';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import type { Column } from './DataTable';
import EmptyState from './EmptyState';
import { ModuleBrowser } from './ModuleBrowser';
import { ModulePreview } from './ModulePreview';
import { rem } from '../lib/rem';
import { ModuleService } from '../services/moduleService';
import type { ContentBlock } from '../../shared/types';
import { hasPermission } from '../../shared/permissions';
import {
  listModuleInsertions,
  moduleCountLabel,
  modulePreviewAnchors,
  type SpecModule,
} from '../../shared/spec-modules';
import { EMPTY_PICKS, type ModulePicks } from '../../shared/spec-assemble';

export interface AssembleSpecEntry {
  id: string;
  number: string;
  title: string;
  blocks: ContentBlock[];
}

export interface AssembleModulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** One or many — more than one shows the stepper. */
  specs: AssembleSpecEntry[];
  /** Pre-fill, keyed by spec id. */
  initialPicks?: Record<string, ModulePicks>;
  /** Every spec's picks; untouched ones are their seed state (no changes). */
  onDone: (picks: Record<string, ModulePicks>) => void;
  /** The signed-in user's permission keys — gate + the browser's table. */
  userPermissions?: string[];
  userName?: string;
  toast?: (msg: string, isError?: boolean) => void;
}

/** `modules.view` — may this user see the library (and so the Modules step)? */
export function canAssembleModules(userPermissions: string[] | undefined): boolean {
  return hasPermission(userPermissions, 'modules.view');
}

const NO_TOAST = () => {};

const COLUMNS: Column<SpecModule>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 320,
    getValue: (m) => m.name || '',
    render: (m) => {
      const anchors = modulePreviewAnchors(m);
      return (
        <span className="sf-mod-row-text" style={{ minWidth: 0 }}>
          <span className="sf-mod-row-name" title={m.name}>
            {m.name}
          </span>
          <span className="sf-mod-row-meta">{moduleCountLabel(m)}</span>
          {anchors.map((a, i) => (
            <span key={i} className="sf-mod-row-anchor">
              {a}
            </span>
          ))}
        </span>
      );
    },
  },
  // Hidden — read by the table's own search only.
  {
    key: 'description',
    label: 'Description',
    width: 200,
    getValue: (m) => m.description || '',
    defaultHidden: true,
  },
  {
    key: 'sourceCsi',
    label: 'Source CSI',
    width: 120,
    getValue: (m) => m.sourceCsi || '',
    defaultHidden: true,
  },
];

/** moduleId → the insertIds of its instances in the spec. */
function seedOf(blocks: ContentBlock[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const ins of listModuleInsertions(blocks || [])) {
    const list = out.get(ins.moduleId);
    if (list) list.push(ins.insertId);
    else out.set(ins.moduleId, [ins.insertId]);
  }
  return out;
}

export function AssembleModulesDialog({
  open,
  onOpenChange,
  specs,
  initialPicks,
  onDone,
  userPermissions,
  userName,
  toast,
}: AssembleModulesDialogProps) {
  const canView = canAssembleModules(userPermissions);
  const [step, setStep] = React.useState(0);
  const [picks, setPicks] = React.useState<Record<string, ModulePicks>>({});
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const [modules, setModules] = React.useState<SpecModule[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  // Each open starts fresh from the prefill — ONE library load per open.
  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setPreviewId(null);
    const seeded: Record<string, ModulePicks> = {};
    for (const s of specs) seeded[s.id] = initialPicks?.[s.id] ?? EMPTY_PICKS;
    setPicks(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open || !canView) return;
    let live = true;
    setLoading(true);
    setError(null);
    ModuleService.list()
      .then((rows) => live && setModules(rows))
      .catch(
        (err: unknown) =>
          live &&
          setError(
            err instanceof Error && err.message ? err.message : 'The module library could not load'
          )
      )
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [open, canView, attempt]);

  const seeds = React.useMemo(() => {
    const m = new Map<string, Map<string, string[]>>();
    for (const s of specs) m.set(s.id, seedOf(s.blocks));
    return m;
  }, [specs]);

  const spec = specs[Math.min(step, Math.max(specs.length - 1, 0))];
  const specId = spec?.id ?? '';
  const seed = seeds.get(specId) ?? new Map<string, string[]>();
  const cur = picks[specId] ?? EMPTY_PICKS;
  const libraryIds = React.useMemo(() => new Set(modules.map((m) => m.id)), [modules]);

  const checked = React.useMemo(() => {
    const ids = new Set<string>();
    const excluded = new Set(cur.excludeInsertIds);
    for (const [moduleId, insertIds] of seed) {
      if (insertIds.some((i) => !excluded.has(i))) ids.add(moduleId);
    }
    for (const m of cur.include) ids.add(m.id);
    return new Set([...ids].filter((id) => libraryIds.has(id)));
  }, [cur, seed, libraryIds]);

  const onSelectionChange = (next: Set<string>) => {
    const byId = new Map(modules.map((m) => [m.id, m]));
    setPicks((prev) => {
      const base = prev[specId] ?? EMPTY_PICKS;
      let include = base.include;
      let exclude = base.excludeInsertIds;
      for (const id of next) {
        if (checked.has(id)) continue;
        const insertIds = seed.get(id);
        if (insertIds) exclude = exclude.filter((i) => !insertIds.includes(i));
        else if (!include.some((m) => m.id === id)) {
          const mod = byId.get(id);
          if (mod) include = [...include, mod];
        }
      }
      for (const id of checked) {
        if (next.has(id)) continue;
        const insertIds = seed.get(id);
        if (insertIds) exclude = [...new Set([...exclude, ...insertIds])];
        else include = include.filter((m) => m.id !== id);
      }
      return { ...prev, [specId]: { include, excludeInsertIds: exclude } };
    });
  };

  const done = () => {
    const out: Record<string, ModulePicks> = {};
    for (const s of specs) out[s.id] = picks[s.id] ?? initialPicks?.[s.id] ?? EMPTY_PICKS;
    onDone(out);
    onOpenChange(false);
  };

  if (!canView) return null;

  const multi = specs.length > 1;
  const previewed = modules.find((m) => m.id === previewId) || null;
  const specLabel = spec ? [spec.number, spec.title].filter(Boolean).join(' ') : '';
  const title = ['Modules', multi ? `${step + 1} of ${specs.length}` : '', specLabel]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Tick the modules this spec should carry. Click a row to tick or untick it; the module
            library itself never changes.
          </DialogDescription>
        </DialogHeader>
        <div
          style={{
            display: 'flex',
            height: '65vh',
            maxHeight: '100%',
            minHeight: 0,
            gap: 'var(--space-3)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            {loading ? (
              <p role="status" className="sf-mod-hint">
                Loading the module library…
              </p>
            ) : error ? (
              <EmptyState
                icon={<Icon icon={Layers} em={2.1538} aria-hidden="true" />}
                title="The module library could not be loaded"
                subtitle={error}
                action={{ label: 'Try again', onClick: () => setAttempt((n) => n + 1) }}
              />
            ) : modules.length === 0 ? (
              <EmptyState
                icon={<Icon icon={Layers} em={2.1538} aria-hidden="true" />}
                title="No modules yet"
              />
            ) : (
              <ModuleBrowser
                hostId="assemble"
                layout="stacked"
                modules={modules}
                userName={userName || ''}
                userPermissions={userPermissions || []}
                toast={toast || NO_TOAST}
                storageKeyBase="module-assemble-table.v3"
                chrome="pick"
                columns={COLUMNS}
                selectable
                selectionMode="checkbox"
                selectedIds={checked}
                onSelectionChange={onSelectionChange}
                onRowClick={(m) => setPreviewId(m.id)}
                searchPlaceholder="Search modules, categories, tags…"
              />
            )}
          </div>
          <aside
            aria-label="Module preview"
            style={{
              width: rem(300),
              flexShrink: 0,
              borderLeft: '1px solid var(--bd)',
              overflow: 'auto',
              padding: 'var(--space-3)',
            }}
          >
            {previewed ? (
              <ModulePreview module={previewed} />
            ) : (
              <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                Click a module to preview the blocks it carries here.
              </div>
            )}
          </aside>
        </div>
        <DialogFooter>
          <span
            role="status"
            style={{
              marginRight: 'auto',
              alignSelf: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
            }}
          >
            {checked.size} selected
          </span>
          {multi && (
            <>
              <Button variant="outline" disabled={step === 0} onClick={() => setStep((n) => n - 1)}>
                Back
              </Button>
              <Button
                variant="outline"
                disabled={step >= specs.length - 1}
                onClick={() => setStep((n) => n + 1)}
              >
                Next
              </Button>
            </>
          )}
          <Button onClick={done}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssembleModulesDialog;
